import React from 'react';
import PropTypes from 'prop-types';
import ProductCard from '@ajat/design-system/ProductCard';
import { ComponentPage, DemoCard } from '../shared';

const IMAGE = require('../../../../assets/cls-dk/products/product-1-1.jpg');
const FRAME_MARK = require('@ajat/design-system/assets/overall/icon-ovr-logotype.svg');

const COLORS = [
  '#6f6f6f', '#2f2f2f', '#aad1ce', '#aad1b2', '#dccee1',
  '#ea9fa0', '#3d88dc', '#c4c4c4', '#080c0a', '#e3ac3f',
  '#eeeeee', '#354c3f', '#621313', '#bfbfb7', '#4c4d3f',
  '#777865', '#d9a9d2', '#a3c5e0', '#f4c7b8', '#8b6f47',
  '#c1d4a7', '#9b3d3d',
];

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-[var(--space-ui-medium-m)]">{children}</div>
);

export const meta = {
  component: ProductCard,
  title: 'Product Card',
  group: 'Commerce',
  importPath: '@ajat/design-system/ProductCard',
  description:
    'Catalogue card built around the Price component. Takes an optional hover image, a colour swatch strip that collapses to "+N more" past its limit, and a selected state used by the package configurator.',
  props: [
    ['image', 'string', '—', 'Primary image source'],
    ['imageAlt', 'string', "''", 'Alt text for the primary image'],
    ['secondImage', 'string', "''", 'Swapped in on hover'],
    ['secondImageAlt', 'string', "''", 'Alt text for the hover image'],
    ['title', 'string', "'Product name'", 'Card title'],
    ['description', 'string', "''", 'Short line under the title'],
    ['priceValue', 'string', "''", 'Current price, passed to Price'],
    ['priceState', "'default' | 'discounted' | 'included'", "'default'", 'Price state'],
    ['originalPrice', 'string', "''", 'Struck-through original price'],
    ['priceBadgeText', 'string', "'Included'", 'Badge text for the included state'],
    ['priceSavingsText', 'string', "''", 'Savings note passed to Price'],
    ['discountBadgeText', 'string', "''", 'Localized discount badge shown top-left while set (e.g. "-20% TILBUD"); the card price then uses the discounted state'],
    ['colors', 'array', '[]', 'Hex strings for the swatch strip'],
    ['moreLabel', 'string', "'more'", 'Suffix on the "+N more" swatch overflow'],
    ['selected', 'bool', 'false', 'Selected state — shows the selected badge'],
    ['selectedLabel', 'string', "'Selected'", "Badge text — pass the brand's word: 'VALGT' (DK), 'VALD' (SE), 'VALITTU' (FI)"],
    ['imageLabels', 'array', "['Image 1', 'Image 2']", 'Accessible names of the two image bullets (mobile swipe)'],
    ['frameMark', 'string', '—', 'Image drawn at the middle of each frame edge, fading in on hover — any brand can pass a glyph (OVER/LL: its logotype); no marks without it'],
    ['imageAspectRatio', 'string', '—', 'CSS aspect-ratio override for the image'],
    ['imageFit', "'cover' | 'contain'", "'cover'", 'contain = the whole image inside the frame (a render / cut-out), cover = a photo filling it'],
    ['href', 'string', '—', 'Renders the card as a link'],
    ['onClick', 'func', '—', 'Click handler'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'In a ProductsGrid',
      code: `import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';

// ProductsGrid renders ProductCard for each item — pass the
// card props straight through as the item objects.
<ProductsGrid
  brand="CLS"
  title="Vores kollektion"
  items={products.map((p) => ({
    image: p.image,
    secondImage: p.hoverImage,
    title: p.name,
    description: p.shortDescription,
    priceValue: \`\${p.price},-\`,
    colors: p.colors,
    onClick: () => openProduct(p.slug),
  }))}
/>`,
    },
  ],
};

export default function ProductCardPage({ brand = 'CLS' }) {
  const selectedLabel = brand === 'ABC' ? 'VALD' : 'VALGT';

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Default price" hint="the last card carries a frameMark — hover it (desktop)">
        <Grid>
          <ProductCard image={IMAGE} imageAlt="Demo product" title="PRODUCT NAME" description="Small" priceValue="159,-" colors={COLORS} frameMark={FRAME_MARK} />
          <ProductCard image={IMAGE} imageAlt="Selected product" title="PRODUCT NAME" description="Small" priceValue="159,-" colors={COLORS} selected selectedLabel={selectedLabel} />
          <ProductCard title="NO IMAGE" description="Medium" priceValue="299,-" colors={COLORS.slice(0, 5)} />
          <ProductCard image={IMAGE} imageAlt="Minimal card" title="MINIMAL CARD" priceValue="49,-" />
        </Grid>
      </DemoCard>

      <DemoCard title="Discounted price">
        <Grid>
          <ProductCard
            image={IMAGE}
            imageAlt="Discounted product"
            title="ON SALE"
            description="Small"
            priceValue="99,-"
            priceState="discounted"
            originalPrice="159,-"
            colors={COLORS}
          />
          <ProductCard
            image={IMAGE}
            imageAlt="Discounted selected"
            title="ON SALE"
            description="Small"
            priceValue="99,-"
            priceState="discounted"
            originalPrice="159,-"
            colors={COLORS.slice(0, 8)}
            selected
            selectedLabel={selectedLabel}
          />
        </Grid>
      </DemoCard>

      <DemoCard title="Included price">
        <Grid>
          <ProductCard
            image={IMAGE}
            imageAlt="Included product"
            title="INCLUDED ITEM"
            description="Small"
            priceValue="0,-"
            priceState="included"
            priceBadgeText="Included"
            colors={COLORS.slice(0, 10)}
          />
          <ProductCard
            image={IMAGE}
            imageAlt="Included selected"
            title="INCLUDED ITEM"
            description="Small"
            priceValue="0,-"
            priceState="included"
            priceBadgeText="Inkluderet"
            selected
            selectedLabel={selectedLabel}
          />
        </Grid>
      </DemoCard>
    </ComponentPage>
  );
}

ProductCardPage.propTypes = {
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall']),
};
